// src/features/coding/utils/jsonUtils.ts
import JSON5 from 'json5';
import yaml from 'js-yaml';

export interface ParseResult {
    data: any | null;
    error: string | null;
}

/**
 * Parses a JSON or JSON5 string.
 * @param jsonString The string to parse.
 * @param strict If true, uses standard JSON.parse. If false, uses JSON5.parse for fault tolerance.
 * @returns An object containing the parsed data or an error message.
 */
export const parseJson = (jsonString: string, strict: boolean): ParseResult => {
    try {
        if (!jsonString.trim()) {
            return { data: null, error: null };
        }
        const data = strict ? JSON.parse(jsonString) : JSON5.parse(jsonString);
        return { data, error: null };
    } catch (e: any) {
        // Provide a more user-friendly error message
        const errorMessage = e.message
            .replace(/'/g, "") // remove quotes
            .replace(/at position \d+/, (match: string) => `at ${e.lineNumber}:${e.columnNumber}`) // Add line/col info if available from JSON5
            .replace(/^JSON5: /, ""); // clean up JSON5 prefix
        return { data: null, error: `Invalid JSON: ${errorMessage}` };
    }
};

/**
 * Formats a JavaScript object into a pretty-printed JSON string.
 * @param jsonObject The object to format.
 * @param indent The number of spaces to use for indentation.
 * @returns A formatted JSON string.
 */
export const formatJson = (jsonObject: any, indent: number): string => {
    if (jsonObject === null || jsonObject === undefined) return '';
    return JSON.stringify(jsonObject, null, indent);
};

/**
 * Converts a JavaScript object into a YAML string.
 * @param jsonObject The object to convert.
 * @returns A YAML string.
 */
export const convertToYaml = (jsonObject: any): string => {
    if (jsonObject === null || jsonObject === undefined) return '';
    try {
        return yaml.dump(jsonObject);
    } catch(e: any) {
        return `YAML Conversion Error: ${e.message}`;
    }
}